import React from 'react';

import { HStack, Text } from '../../index';

interface DateTimeCellProps {
  value: unknown;
  children: React.ReactNode;
  showTime?: boolean;
  showDate?: boolean;
  dateFormat?: Intl.DateTimeFormatOptions;
  timeFormat?: Intl.DateTimeFormatOptions;
  locale?: string;
  separator?: string;
}

const formatDate = (date: Date, format: Intl.DateTimeFormatOptions, locale: string): string => {
  try {
    return new Intl.DateTimeFormat(locale, format).format(date);
  } catch {
    return date.toLocaleDateString(locale, format);
  }
};

const isValidDate = (value: unknown): value is Date | string | number => {
  if (value === null || value === undefined) return false;

  const date = new Date(value as Date | string | number);
  return !isNaN(date.getTime());
};

export const DateTimeCell: React.FC<DateTimeCellProps> = ({
  value,
  children,
  showTime = true,
  showDate = true,
  dateFormat = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  timeFormat = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
  locale = 'en-US',
  separator = ' • ',
}) => {
  // If not a valid date, render children as fallback
  if (!isValidDate(value)) {
    return <>{children}</>;
  }

  const date = new Date(value as Date | string | number);

  // Format date and time separately
  const dateStr = showDate ? formatDate(date, dateFormat, locale) : '';
  const timeStr = showTime ? formatDate(date, timeFormat, locale) : '';

  // If only showing date or only showing time, return single component
  if (showDate && !showTime) {
    return (
      <Text color="#1F2937" fontSize="sm" fontWeight="medium">
        {dateStr}
      </Text>
    );
  }

  if (!showDate && showTime) {
    return (
      <Text color="#4B5563" fontSize="sm">
        {timeStr}
      </Text>
    );
  }

  // Show both date and time with separator
  return (
    <HStack gap="4px" alignItems="center">
      <Text color="#1F2937" fontSize="sm" fontWeight="medium">
        {dateStr}
      </Text>
      <Text color="#6B7280" fontSize="xs">
        {separator}
      </Text>
      <Text color="#4B5563" fontSize="sm">
        {timeStr}
      </Text>
    </HStack>
  );
};
